import { useQuery } from '@tanstack/react-query';
import { fetchBillDataFromSheets, fetchBillPagesFromSheets } from '../services/googleSheets';
import { getFallbackData } from '../utils/billConfig';
import { countrySlugToName } from '../utils/stateUtils';
import { WhyItMatters } from '../types/whyItMatters';

interface BillPageConfig {
  route: string;
  pageTitle: string;
  secondaryTitle: string;
  shortDescription: string;
  seoTitle: string;
  seoDescription: string;
  billId?: string;
  billUrl?: string;
  whyItMatters?: WhyItMatters;
}

interface BillUrlInfo {
  billUrl: string;
  googleDriveUrl: string;
  buttonText: string;
  billStatus: string;
}

// Hook to fetch bill pages from Google Sheets
export const useBillPages = () => {
  return useQuery({
    queryKey: ['billPages'],
    queryFn: fetchBillPagesFromSheets,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to fetch bill data from Google Sheets
export const useBillDataSheets = () => {
  return useQuery({
    queryKey: ['billDataSheets'],
    queryFn: fetchBillDataFromSheets,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Main hook to get bill configuration by slug
export const useBillConfig = (billSlug: string | undefined) => {
  const { data: billPages, isLoading: billPagesLoading } = useBillPages();

  const query = useQuery({
    queryKey: ['billConfig', billSlug],
    queryFn: async (): Promise<BillPageConfig | null> => {
      if (!billSlug) return null;

      // Find the matching bill using the exact billSlug from the Page Link column
      const foundBill = billPages?.find((bill) => bill.route === billSlug);

      // Always try to fetch billIndex.json to get whyItMatters data
      let whyItMattersData: WhyItMatters | undefined;
      try {
        const response = await fetch('/bills/billIndex.json');
        if (response.ok) {
          const billIndex = await response.json();
          const billEntry = billIndex.bills[billSlug];
          if (billEntry?.whyItMatters) {
            whyItMattersData = billEntry.whyItMatters;
          }

          // If Google Sheets didn't have the bill, build config from billIndex
          if (!foundBill && billEntry) {
            // Check static config for bill-specific text before using generic fallback
            const staticConfig = getFallbackData().find((bill) => bill.route === billSlug);
            return {
              route: billSlug,
              pageTitle: billEntry.title,
              secondaryTitle: staticConfig?.secondaryTitle ?? 'State-specific judicial reform legislation',
              shortDescription:
                staticConfig?.shortDescription ??
                `${billEntry.title} - Comprehensive judicial reform legislation worldwide.`,
              seoTitle: staticConfig?.seoTitle ?? `RobertGarza.us Family Court Reform - ${billEntry.title}`,
              seoDescription:
                staticConfig?.seoDescription ??
                `Explore the ${billEntry.title} - state-specific judicial reform legislation aimed at improving family court systems.`,
              billId: billSlug.toUpperCase(),
              whyItMatters: whyItMattersData,
            };
          }
        }
      } catch (error) {
        console.error('Error fetching local bill index:', error);
      }

      if (foundBill) {
        return {
          ...foundBill,
          whyItMatters: whyItMattersData,
        };
      }

      // Final fallback: Check static fallback data
      const fallbackBill = getFallbackData().find((bill) => bill.route === billSlug);
      if (fallbackBill) {
        return fallbackBill;
      }

      return null;
    },
    // A failed Sheets request must still allow the local index and static fallbacks to resolve.
    enabled: !!billSlug && !billPagesLoading,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Return query result with additional loading state that accounts for parent query
  // isPending is true when query is disabled OR loading, preventing "not found" flash
  return {
    ...query,
    // True if either parent is loading OR this query is loading/pending
    isPending: billPagesLoading || query.isLoading || query.isPending,
    // True only after query has resolved (not disabled, not loading)
    isResolved: !billPagesLoading && !query.isLoading && !query.isPending,
  };
};

// Helper function to convert state slug to title case for Google Sheets lookup
const convertStateSlugToTitle = (stateSlug: string): string => {
  return stateSlug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// State-reactive hook to get bill URL information
export const useBillUrlInfo = (
  billTitle: string | undefined,
  currentState: string | undefined,
  billSlug: string | undefined,
  currentCountry: string = 'united-states',
) => {
  const { data: billData, isLoading: billDataLoading, error: billDataError } = useBillDataSheets();

  const query = useQuery({
    queryKey: ['billUrlInfo', billTitle, currentState, billSlug, currentCountry],
    queryFn: async (): Promise<BillUrlInfo> => {
      if (!billTitle || !billData) {
        return {
          billUrl: '',
          googleDriveUrl: '',
          buttonText: 'View Bill Proposal',
          billStatus: '',
        };
      }

      // Find all matching bills with the same title (case-insensitive)
      // First filter by country to ensure correct jurisdiction
      const countryName = countrySlugToName(currentCountry) || currentCountry;
      const countryFiltered = billData.filter((bill) => {
        const billCountry = bill.country?.toLowerCase().trim();
        return billCountry === countryName.toLowerCase();
      });
      const matchingBills = countryFiltered.filter((bill) => {
        const billTitleLower = bill.billTitle.toLowerCase();
        const searchTitleLower = billTitle.toLowerCase();

        // Regular includes matching
        const regularMatch = billTitleLower.includes(searchTitleLower) || searchTitleLower.includes(billTitleLower);

        // Special case for Child Support Integrity Act - handle "Integrety" typo in spreadsheet
        const isChildSupportIntegrityBill =
          billTitleLower.includes('child support') &&
          (billTitleLower.includes('integrity') || billTitleLower.includes('integrety'));
        const isChildSupportIntegritySearch =
          searchTitleLower.includes('child support') &&
          (searchTitleLower.includes('integrity') || searchTitleLower.includes('integrety'));

        if (isChildSupportIntegrityBill && isChildSupportIntegritySearch) {
          return true;
        }

        // Special case for reimbursement bills - match both "alienation" and "alienating behavior" variants
        const isReimbursementBill = billTitleLower.includes('reimbursement') && billTitleLower.includes('parental');
        const isReimbursementSearch =
          searchTitleLower.includes('reimbursement') && searchTitleLower.includes('parental');

        if (isReimbursementBill && isReimbursementSearch) {
          // More flexible matching for reimbursement bills
          const billWords = billTitleLower.split(' ');
          const searchWords = searchTitleLower.split(' ');

          // Check if most key words match
          const keyWords = ['reimbursement', 'fees', 'parental'];
          const matchingKeyWords = keyWords.filter((word) => billWords.includes(word) && searchWords.includes(word));

          return matchingKeyWords.length >= 3; // Match if at least 3 key words match
        }

        return regularMatch;
      });

      let selectedBill = null;
      let billStatus = '';

      if (currentState) {
        // Convert state slug to title case for Google Sheets lookup
        const stateTitleCase = convertStateSlugToTitle(currentState);

        // Find state-specific bill entry - try both formats
        selectedBill = matchingBills.find(
          (bill) =>
            bill.state.toLowerCase() === stateTitleCase.toLowerCase() ||
            bill.state.toLowerCase() === currentState.toLowerCase(),
        );

        if (selectedBill) {
          billStatus = selectedBill.billStatus;
        }
      }

      let billUrl = '';
      let googleDriveUrl = '';
      let buttonText = 'View Bill Proposal';

      // STEP 1: Check Google Sheets for URLs
      if (selectedBill) {
        // Check for Legislator Pack URL (Google Drive)
        if (selectedBill.googleDriveUrl && selectedBill.googleDriveUrl.trim() !== '') {
          const googleDriveUrlValue = selectedBill.googleDriveUrl.trim();
          const hasProtocol =
            googleDriveUrlValue.toLowerCase().startsWith('http://') ||
            googleDriveUrlValue.toLowerCase().startsWith('https://');
          googleDriveUrl = hasProtocol ? googleDriveUrlValue : `https://${googleDriveUrlValue}`;
        }

        // Check for Bill Text URL (external legislative site)
        if (selectedBill.billUrl && selectedBill.billUrl.trim() !== '') {
          const billUrlValue = selectedBill.billUrl.trim();
          const hasProtocol =
            billUrlValue.toLowerCase().startsWith('http://') || billUrlValue.toLowerCase().startsWith('https://');
          const looksLikeUrl = hasProtocol || billUrlValue.includes('.') || billUrlValue.includes('/');
          const isStatusValue = ['not started', 'in progress', 'completed', 'pending', 'draft'].includes(
            billUrlValue.toLowerCase(),
          );

          if (looksLikeUrl && !isStatusValue) {
            // External legislative URL - use "View Bill" and link to external URL
            const finalUrl = hasProtocol ? billUrlValue : `https://${billUrlValue}`;
            billUrl = finalUrl;
            buttonText = 'View Bill';

            return { billUrl, googleDriveUrl, buttonText, billStatus };
          }
        }
      }

      // STEP 2: Check for local content or Google Drive docx
      const hasLocalContent = await checkForLocalBillContent(billSlug, currentState);
      const hasMatchingDocx = await checkForDocxInDrive(billTitle, currentState);

      if (hasLocalContent || hasMatchingDocx) {
        // Found content (either local or .docx) - use "View Bill Proposal" and link to bill text section
        billUrl = 'bill-text-available'; // Signal that bill text should be shown
        buttonText = 'View Bill Proposal';
      } else if (googleDriveUrl) {
        // No bill text available, but Legislator Pack exists - change CTA to "View Legislator Pack"
        buttonText = 'View Legislator Pack';
      }

      return { billUrl, googleDriveUrl, buttonText, billStatus };
    },
    enabled: !!billTitle && !billDataLoading && !billDataError,
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter since state-dependent)
    gcTime: 5 * 60 * 1000,
  });

  // The URL query waits for Sheets; expose that dependency's state to the page.
  // Otherwise a failed or pending Sheets request looks like a missing legislator pack.
  return {
    ...query,
    isLoading: billDataLoading || query.isLoading,
    error: billDataError || query.error,
  };
};

// Helper functions (moved from component)
const checkForLocalBillContent = async (billSlug: string | undefined, currentState?: string): Promise<boolean> => {
  try {
    if (!billSlug) {
      return false;
    }

    const billIndexResponse = await fetch('/bills/billIndex.json');
    if (!billIndexResponse.ok) {
      return false;
    }

    const billIndex = await billIndexResponse.json();

    // Use the billSlug directly from URL params
    const billEntry = billIndex.bills[billSlug];
    if (!billEntry) {
      return false;
    }

    // Load the local bill file
    const billResponse = await fetch(billEntry.localFile);
    if (!billResponse.ok) {
      return false;
    }

    const billData = await billResponse.json();

    // Check if this bill has state-specific content
    if (currentState) {
      const stateKey = currentState.toLowerCase().replace(/-/g, ' ');
      if (billData.states && billData.states[stateKey] && billData.states[stateKey].hasDocument) {
        return true;
      }
    }

    // Check if it has generic content as fallback
    if (billData.content && billData.hasDocument) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

const checkForDocxInDrive = async (billTitle: string, currentState?: string): Promise<boolean> => {
  try {
    const apiKey = process.env.GOOGLE_DRIVE_API_KEY;
    const folderId = process.env.GOOGLE_DRIVE_FOLDER_ID;

    if (!apiKey || !folderId) {
      return false;
    }

    // Create a mapping from bill titles to expected file name patterns (case-insensitive)
    const billTitleToFileName: Record<string, string> = {
      'time taken time back': 'Time Taken Time Back',
      'three strikes': 'Three Strikes',
      'justice delayed is justice denied': 'Justice Delayed is Justice Denied',
      'a new start at equality': 'A New Start at Equality',
      'child support reform': 'Child Support Reform',
      'cameras in courtrooms': 'Cameras in Courtrooms',
      'the litigant access and economy act': 'The Litigant Access and Economy Act',
      'appealable temporary orders': 'Appealable Temporary Orders',
    };

    // Get the expected file name pattern for this bill (case-insensitive lookup)
    const billTitleLower = billTitle.toLowerCase();
    const expectedFileName = billTitleToFileName[billTitleLower];

    if (!expectedFileName) {
      return false;
    }

    // If user has a state, check for state-specific .docx files
    if (currentState) {
      // First try exact case match
      const exactFileName = `${currentState}_${expectedFileName}`;
      let stateSpecificQuery = `name='${exactFileName}.docx' and mimeType='application/vnd.openxmlformats-officedocument.wordprocessingml.document' and '${folderId}' in parents and trashed=false`;

      let stateResponse = await fetch(
        `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(stateSpecificQuery)}&key=${apiKey}&fields=files(id,name)`,
      );

      if (stateResponse.ok) {
        let stateData = await stateResponse.json();

        if (stateData.files && stateData.files.length > 0) {
          return true;
        }
      }

      // If no exact match, try case-insensitive search
      const lowerFileName = `${currentState.toLowerCase()}_${expectedFileName.toLowerCase()}`;
      stateSpecificQuery = `name contains '${lowerFileName}' and name contains '.docx' and mimeType='application/vnd.openxmlformats-officedocument.wordprocessingml.document' and '${folderId}' in parents and trashed=false`;

      stateResponse = await fetch(
        `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(stateSpecificQuery)}&key=${apiKey}&fields=files(id,name)`,
      );

      if (stateResponse.ok) {
        const stateData = await stateResponse.json();

        if (stateData.files && stateData.files.length > 0) {
          // Filter to find the most exact match
          const bestMatch = stateData.files.find((file: any) => {
            const fileName = file.name.toLowerCase();
            const expectedLower = `${lowerFileName}.docx`;
            return fileName === expectedLower;
          });

          if (bestMatch) {
            return true;
          }
        }
      }
    }

    return false;
  } catch {
    return false;
  }
};
